import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
	type ReactNode,
} from "react";

export type Theme = "light" | "dark" | "system";

interface ThemeContextValue {
	theme: Theme;
	resolved: "light" | "dark";
	setTheme: (t: Theme) => void;
}

const STORAGE_KEY = "kartenwerk-theme";
const ThemeContext = createContext<ThemeContextValue | null>(null);

function resolveTheme(theme: Theme): "light" | "dark" {
	if (theme === "light") return "light";
	if (theme === "dark") return "dark";
	return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
	const [theme, setThemeState] = useState<Theme>(() => {
		try {
			const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
			if (stored === "light" || stored === "dark" || stored === "system") return stored;
		} catch {
			/* ignore */
		}
		return "system";
	});
	const [resolved, setResolved] = useState<"light" | "dark">(() =>
		typeof window === "undefined" ? "dark" : resolveTheme(theme),
	);

	const apply = useCallback((t: Theme) => {
		const r = resolveTheme(t);
		document.documentElement.classList.toggle("dark", r === "dark");
		setResolved(r);
	}, []);

	useEffect(() => {
		apply(theme);
		if (theme !== "system") return;
		const mq = window.matchMedia("(prefers-color-scheme: dark)");
		const onChange = () => apply("system");
		mq.addEventListener("change", onChange);
		return () => mq.removeEventListener("change", onChange);
	}, [theme, apply]);

	const setTheme = useCallback((t: Theme) => {
		setThemeState(t);
		try {
			localStorage.setItem(STORAGE_KEY, t);
		} catch {
			/* ignore */
		}
	}, []);

	return (
		<ThemeContext.Provider value={{ theme, resolved, setTheme }}>
			{children}
		</ThemeContext.Provider>
	);
}

export function useTheme(): ThemeContextValue {
	const ctx = useContext(ThemeContext);
	if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
	return ctx;
}
